import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send } from 'lucide-react';
export function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    goal: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      consent: e.target.checked
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        goal: '',
        consent: false
      });
    }, 1500);
  };
  return <section id="contact" className="py-20 md:py-28 bg-cream px-6" data-id="element-38">
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-39">
        {/* Left Column - Info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-40">
          <span className="text-copper font-bold text-sm tracking-wider uppercase mb-3 block" data-id="element-41">
            Get a Personalized Strategy
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-forest mb-6" data-id="element-42">
            See If an IUL Is Right For You
          </h2>
          <p className="text-charcoal text-lg leading-relaxed mb-8" data-id="element-43">
            Get personalized indexed universal life insurance quotes—no
            obligation, no pressure. Xander will walk you through how an IUL
            could fit into your overall retirement picture.
          </p>
          <p className="text-sage font-medium bg-white/50 inline-block px-4 py-2 rounded-lg border border-sage/20" data-id="element-44">
            Typical call length: 20–30 minutes. Available via Zoom or phone, at
            your convenience.
          </p>
        </motion.div>

        {/* Right Column - Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-white p-6 md:p-8 rounded-2xl shadow-soft border border-gray-100" data-id="element-45">
          {isSubmitted ? <div className="text-center py-12" data-id="element-46">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-47">
                <Send size={32} data-id="element-48" />
              </div>
              <h3 className="text-2xl font-bold text-forest mb-2" data-id="element-49">
                Request Received!
              </h3>
              <p className="text-charcoal" data-id="element-50">
                Xander will be in touch shortly to schedule your consultation.
              </p>
              <button onClick={() => setIsSubmitted(false)} className="mt-6 text-copper font-medium hover:underline" data-id="element-51">
                Send another request
              </button>
            </div> : <>
              <h3 className="text-2xl font-bold text-forest mb-6" data-id="element-52">
                Request Your Free IUL Quote
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-53">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-54">
                  <div data-id="element-55">
                    <label htmlFor="firstName" className="block text-sm font-medium text-charcoal mb-1" data-id="element-56">
                      First Name
                    </label>
                    <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper outline-none transition-colors bg-warm-white" placeholder="Jane" data-id="element-57" />
                  </div>
                  <div data-id="element-58">
                    <label htmlFor="lastName" className="block text-sm font-medium text-charcoal mb-1" data-id="element-59">
                      Last Name
                    </label>
                    <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper outline-none transition-colors bg-warm-white" placeholder="Doe" data-id="element-60" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-61">
                  <div data-id="element-62">
                    <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-1" data-id="element-63">
                      Email
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper outline-none transition-colors bg-warm-white" placeholder="jane@example.com" data-id="element-64" />
                  </div>
                  <div data-id="element-65">
                    <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1" data-id="element-66">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper outline-none transition-colors bg-warm-white" placeholder="(555) 123-4567" data-id="element-67" />
                  </div>
                </div>

                <div data-id="element-68">
                  <label htmlFor="goal" className="block text-sm font-medium text-charcoal mb-1" data-id="element-69">
                    What's your primary retirement goal?
                  </label>
                  <textarea id="goal" name="goal" rows={3} value={formData.goal} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper outline-none transition-colors bg-warm-white resize-none" placeholder="e.g. Tax-free income, market protection..." data-id="element-70"></textarea>
                </div>

                <div className="flex items-start gap-3 pt-2" data-id="element-71">
                  <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleCheckboxChange} className="mt-1 w-4 h-4 text-copper border-gray-300 rounded focus:ring-copper" data-id="element-72" />
                  <label htmlFor="consent" className="text-xs text-sage leading-relaxed" data-id="element-73">
                    By submitting, you agree that Xander Winther may contact you
                    by phone, text, or email about indexed universal life
                    insurance and related retirement strategies. You can opt out
                    at any time.
                  </label>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-copper text-white font-bold py-4 rounded-full mt-4 hover:bg-[#A6683C] transition-all hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-74">
                  {isSubmitting ? 'Processing...' : 'Get My Free Quote'}
                </button>
              </form>
            </>}
        </motion.div>
      </div>
    </section>;
}